// ============================================================================
// AUDITOR DASHBOARD - ENTERPRISE EDITION
// ============================================================================
// Purpose: Comprehensive auditor dashboard with analytics and insights
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { auditApi, AuditFinding } from '../../audit/api';
import { Award, FileText, CheckCircle, AlertTriangle, TrendingUp, Activity, Target, Zap, RefreshCw } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

interface AuditorStats {
  assigned_frameworks: number;
  pending_reviews: number;
  completed_reviews: number;
  overdue_reviews: number;
  active_alerts: number;
  total_certifications: number;
  active_certifications: number;
  expiring_certifications: number;
  compliance_score_avg: number;
  total_companies: number;
  high_risk_findings: number;
  review_efficiency: number;
}

const AuditorDashboard: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const isExternalAuditor = hasPermission('compliance.certify');

  const [stats, setStats] = useState<AuditorStats | null>(null);
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [loading, setLoading] = useState(true);

  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>(() => {
    return (localStorage.getItem('auditorDashboardTimeRange') as '7d' | '30d' | '90d') || '30d';
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    localStorage.setItem('auditorDashboardTimeRange', timeRange);
  }, [timeRange]);

  // Filter findings based on time range
  const filteredFindings = useMemo(() => {
    const now = new Date();
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    return findings.filter(finding => new Date(finding.created_at) >= cutoff);
  }, [findings, timeRange]);

  // Calculate stats based on filtered findings
  const filteredStats = useMemo(() => {
    if (!stats) return stats;

    return {
      ...stats,
      active_alerts: filteredFindings.filter(f => f.status === 'open' && f.severity === 'critical').length,
      high_risk_findings: filteredFindings.filter(f => f.severity === 'critical').length,
    };
  }, [stats, filteredFindings]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get assigned companies and calculate stats
      const companies = await auditApi.getAssignedCompanies();
      const frameworks = await auditApi.getFrameworksForReview();
      const auditFindings = await auditApi.getAuditFindings();

      // Calculate actual stats from data
      const totalReviews = frameworks.filter(f => f.status === 'completed' || f.status === 'in_progress').length;
      const completedReviews = frameworks.filter(f => f.status === 'completed').length;
      const reviewEfficiency = totalReviews > 0 ? Math.round((completedReviews / totalReviews) * 100) : 0;
      const validScores = frameworks.map(f => f.compliance_score).filter(score => typeof score === 'number' && !isNaN(score));
      const complianceScoreAvg = validScores.length > 0 ? Math.round(validScores.reduce((sum, score) => sum + Number(score), 0) / validScores.length) : 0;

      // Overdue = overdue status frameworks
      const overdueReviews = frameworks.filter(f => f.status === 'overdue').length;

      // Active certifications = frameworks with status 'certified' or 'completed'
      const certifiedFrameworks = frameworks.filter(f => f.status === 'certified' || (f.compliance_score === 100 && f.status === 'completed'));
      const activeCertifications = certifiedFrameworks.length;

      const statsData: AuditorStats = {
        assigned_frameworks: frameworks.length,
        pending_reviews: frameworks.filter(f => f.status !== 'completed' && f.status !== 'certified').length,
        completed_reviews: completedReviews,
        overdue_reviews: overdueReviews,
        active_alerts: auditFindings.filter(f => f.status === 'open' && f.severity === 'critical').length,
        total_certifications: activeCertifications,
        active_certifications: activeCertifications,
        expiring_certifications: 0,
        compliance_score_avg: complianceScoreAvg,
        total_companies: companies.length,
        high_risk_findings: auditFindings.filter(f => f.severity === 'critical').length,
        review_efficiency: reviewEfficiency
      };

      setStats(statsData);
      setFindings(auditFindings);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setStats({
        assigned_frameworks: 0,
        pending_reviews: 0,
        completed_reviews: 0,
        overdue_reviews: 0,
        active_alerts: 0,
        total_certifications: 0,
        active_certifications: 0,
        expiring_certifications: 0,
        compliance_score_avg: 0,
        total_companies: 0,
        high_risk_findings: 0,
        review_efficiency: 0
      });
      setFindings([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-600 font-medium text-sm">Loading auditor dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">
              {isExternalAuditor ? 'External Auditor Portal' : 'Auditor Dashboard'}
            </h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">
              {isExternalAuditor
                ? 'Independent compliance review — blind audit enabled'
                : 'Comprehensive oversight of compliance frameworks and certifications'}
            </p>
            {isExternalAuditor && (
              <p className="text-xs text-purple-600 font-medium mt-1">
                Auditor: {user?.first_name} {user?.last_name} &nbsp;|&nbsp; No company affiliation
              </p>
            )}
          </div>
          <button
            onClick={loadDashboardData}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Time Range Selector */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-slate-900">Analytics Overview</h2>
              <div className="flex bg-slate-50 rounded-lg p-1 border border-slate-200">
                {[
                  { key: '7d', label: '7 Days' },
                  { key: '30d', label: '30 Days' },
                  { key: '90d', label: '90 Days' }
                ].map((range) => (
                  <button
                    key={range.key}
                    onClick={() => setTimeRange(range.key as any)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${
                      timeRange === range.key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Activity size={14} />
              <span>Last updated: {new Date().toLocaleTimeString()}</span>
            </div>
          </div>
        </div>

        {/* Key Metrics Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FileText size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Assigned Frameworks</p>
                  <p className="text-xs text-slate-400">{stats.pending_reviews} pending review</p>
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.assigned_frameworks}</p>
              <p className="text-xs text-slate-500 mt-1">Active oversight</p>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                  <Target size={18} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Compliance Score</p>
                  <p className={`text-xs font-medium ${
                    stats.compliance_score_avg >= 80 ? 'text-green-600' : stats.compliance_score_avg >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {stats.compliance_score_avg >= 80 ? 'Above target' : stats.compliance_score_avg >= 60 ? 'Approaching target' : 'Below target'}
                  </p>
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.compliance_score_avg}%</p>
              <p className="text-xs text-slate-500 mt-1">Average across frameworks</p>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Award size={18} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Active Certifications</p>
                  <p className={`text-xs font-medium ${stats.expiring_certifications > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {stats.expiring_certifications > 0 ? `${stats.expiring_certifications} expiring soon` : 'None expiring soon'}
                  </p>
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.active_certifications}</p>
              <p className="text-xs text-slate-500 mt-1">Currently valid</p>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Zap size={18} className="text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Review Efficiency</p>
                  <p className="text-xs text-slate-400">On track</p>
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.review_efficiency}%</p>
              <p className="text-xs text-slate-500 mt-1">Completion rate</p>
            </div>
          </div>
        )}

        {/* Secondary Metrics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Review Status</h3>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs text-slate-500">{stats.pending_reviews} pending</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Completed</span>
                  <span className="text-sm font-semibold text-green-600">{stats.completed_reviews}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Pending</span>
                  <span className="text-sm font-semibold text-yellow-600">{stats.pending_reviews}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Overdue</span>
                  <span className="text-sm font-semibold text-red-600">{stats.overdue_reviews}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Risk Assessment</h3>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-xs text-slate-500">{filteredStats?.high_risk_findings || 0} critical</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Critical Findings</span>
                  <span className="text-sm font-semibold text-red-600">{filteredStats?.high_risk_findings || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Active Alerts</span>
                  <span className="text-sm font-semibold text-yellow-600">{filteredStats?.active_alerts || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Companies Monitored</span>
                  <span className="text-sm font-semibold text-blue-600">{stats.total_companies}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Certification Pipeline</h3>
                <TrendingUp className="text-green-600" size={16} />
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Total Issued</span>
                  <span className="text-sm font-semibold text-slate-900">{stats.total_certifications}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Active</span>
                  <span className="text-sm font-semibold text-green-600">{stats.active_certifications}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Expiring Soon</span>
                  <span className="text-sm font-semibold text-yellow-600">{stats.expiring_certifications}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Action */}
        <div className="mb-6">
          <button
            onClick={() => window.location.href = '/audit/frameworks'}
            className="w-full bg-white rounded-3xl border border-slate-100 p-4 text-left hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FileText size={18} className="text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-900">Framework Reviews</h3>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700">
                      {stats?.pending_reviews || 0} pending
                    </span>
                  </div>
                  <p className="text-sm text-slate-500">Review assigned compliance frameworks and evidence</p>
                </div>
              </div>
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        </div>

        {/* Recent Activity & Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Activity */}
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900">Recent Activity</h3>
              <button className="text-blue-600 hover:text-blue-700 text-xs font-medium">
                View All
              </button>
            </div>
            <div className="space-y-3">
              {findings.slice(0, 5).map((finding) => (
                <div key={finding.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                    finding.finding_type === 'positive_practice' ? 'bg-green-500' :
                    finding.finding_type === 'non_conformity' ? 'bg-red-500' :
                    'bg-blue-500'
                  }`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {finding.finding_type === 'positive_practice' ? 'Approved practice' :
                       finding.finding_type === 'non_conformity' ? 'Non-conformity found' :
                       'Observation noted'} for Company {finding.company_id}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(finding.created_at).toLocaleDateString()} • {finding.severity} priority
                    </p>
                  </div>
                </div>
              ))}
              {findings.length === 0 && (
                <div className="text-center py-8">
                  <Activity className="mx-auto text-slate-300 mb-3" size={28} />
                  <p className="text-slate-500 text-sm">No recent activity</p>
                </div>
              )}
            </div>
          </div>

          {/* Active Alerts */}
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900">Active Alerts</h3>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-slate-500">{stats?.active_alerts || 0} active</span>
              </div>
            </div>
            <div className="space-y-3">
              {filteredStats?.active_alerts && filteredStats.active_alerts > 0 ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-red-600 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm font-medium text-red-900">Critical Findings Require Attention</p>
                      <p className="text-xs text-red-700 mt-1">
                        {filteredStats?.active_alerts || 0} critical findings need immediate review
                      </p>
                      <button className="mt-2 text-xs font-medium text-red-700 hover:text-red-900 underline">
                        Review Now
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="mx-auto text-green-400 mb-3" size={28} />
                  <p className="text-green-600 font-medium text-sm">All Clear</p>
                  <p className="text-slate-500 text-xs">No active alerts at this time</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditorDashboard;
